/**
 * Prawa kolumna – szczegóły oferty (metadane)
 */

import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Skeleton, Switch, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'date-fns';

import type { ISalesCrmOffer } from 'api/endpoints/useGetSalesCrmClientOffer';
import { usePutSalesCrmOffer } from 'api';
import DatePicker from 'components/controls/DatePicker';

import styles from 'theme/components/containers/TraderClientCardContainer/TraderClientData/TraderClientData.module.scss';

interface ITraderClientOfferDetailsProps {
  offer?: ISalesCrmOffer | null;
  isLoading?: boolean;
  /** Wymagane dla edycji validUntil (PUT /offers/:id). */
  clientId?: number;
  offerId?: string;
}

const formatDateTime = (iso: string | undefined): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, 'dd.MM.yyyy HH:mm');
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, 'dd.MM.yyyy');
};

const TraderClientOfferDetails: React.FC<ITraderClientOfferDetailsProps> = ({
  offer,
  isLoading = false,
  clientId,
  offerId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEditValidUntil =
    typeof clientId === 'number' && clientId > 0 && typeof offerId === 'string' && offerId.length > 0;

  const [validUntilDraft, setValidUntilDraft] = useState<string>('');
  useEffect(() => {
    setValidUntilDraft(offer?.valid_until_iso ?? '');
  }, [offer?.valid_until_iso]);

  const { mutate: putOffer, isLoading: isSavingValidUntil } = usePutSalesCrmOffer(
    clientId ?? 0,
    offerId ?? '',
    {
      onSuccess: () => {
        if (clientId && offerId) {
          queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId]);
        }
      }
    }
  );

  // DatePicker wywoluje onChange ze stringiem 'yyyy-MM-dd' przy wyborze lub null przy clear.
  // Pusty string "" -> backend interpretuje jako "wyczysc" (OfferVersion.validUntil = NULL).
  const handleValidUntilChange = (newValue: string | null) => {
    const value = newValue ?? '';
    const current = offer?.valid_until_iso ?? '';
    if (value === current) return;
    if (value !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    setValidUntilDraft(value);
    putOffer({ validUntil: value });
  };

  const handleIsPeriodicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    if (next === Boolean(offer?.is_periodic)) return;
    putOffer({ isPeriodic: next });
  };

  const wrapperClass = classnames(
    styles.detailsWrapper,
    'StylePath-Containers-TraderClientOfferContainer-TraderClientOfferDetails'
  );

  if (isLoading) {
    return (
      <div className={wrapperClass}>
        <section className={styles.section}>
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 1.5 }} />
          <Skeleton variant="text" width="82%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="76%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="38%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="68%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="74%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="64%" height={20} />
        </section>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <div className={wrapperClass}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('Szczegóły oferty')}</h3>
        <p className={styles.row}>
          <span className={styles.label}>{t('Data utworzenia')}:</span>{' '}
          <span className={styles.value}>{formatDateTime(offer.created_at)}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Data obowiązywania')}:</span>{' '}
          {canEditValidUntil ? (
            <DatePicker
              date={validUntilDraft || undefined}
              onChange={handleValidUntilChange}
              clearable
              disabled={isSavingValidUntil}
            />
          ) : (
            <span className={styles.value}>{formatDate(offer.valid_until)}</span>
          )}
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Oferta okresowa')}:</span>{' '}
          {canEditValidUntil ? (
            <>
              <Switch
                checked={Boolean(offer.is_periodic)}
                disabled={isSavingValidUntil}
                onChange={handleIsPeriodicChange}
                size="small"
                inputProps={{ 'aria-label': t('Oferta okresowa') }}
              />
              <Tooltip
                title={t(
                  'Włączone - klient może wielokrotnie skorzystać z oferty do końca okresu ważności. Wyłączone - klient korzysta z oferty tylko raz po akceptacji.'
                )}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
              </Tooltip>
            </>
          ) : (
            <span className={styles.value}>{offer.is_periodic ? t('Tak') : t('Nie')}</span>
          )}
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Wersja')}:</span>{' '}
          <span className={styles.value}>{offer.version ?? '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Autor oferty')}:</span>{' '}
          <span className={styles.value}>{offer.author_name ?? '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Ostatnia zmiana')}:</span>{' '}
          <span className={styles.value}>{formatDateTime(offer.last_modified_at)}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Zmieniono przez')}:</span>{' '}
          <span className={styles.value}>{offer.last_modified_by ?? '–'}</span>
        </p>
      </section>
    </div>
  );
};

export default TraderClientOfferDetails;
